import React from 'react';
import SafeIcon from '../../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiBriefcase, FiZap, FiHome, FiCreditCard, FiCalendar, FiTrendingDown} = FiIcons;

export const MatchTool = ({shared, patch}) => (
  <div className="space-y-3">
    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Do you have an employer match?</p>
    <div className="flex gap-2">
      {['Yes', 'No'].map(option => (
        <button key={option} onClick={() => patch({hasMatch: option})} className={`flex-1 py-3 rounded-lg font-black text-[10px] uppercase tracking-widest ${shared.hasMatch === option ? 'bg-emerald-600 text-white' : 'bg-slate-50 text-slate-400 border border-slate-200'}`}>{option}</button>
      ))}
    </div>
    {shared.hasMatch === 'Yes' && <p className="p-3 bg-emerald-50 rounded-xl text-[10px] font-bold text-emerald-800">Contribute enough to receive the full match. Check the exact matching formula in your benefits portal.</p>}
    {shared.hasMatch === 'No' && <p className="p-3 bg-slate-50 rounded-xl text-[10px] font-bold text-slate-600">No match available? Continue to high-interest debt.</p>}
  </div>
);

export const MatchVisual = () => (
  <div className="bg-emerald-50/60 p-4 sm:p-6 rounded-2xl border border-emerald-100 space-y-4">
    <div className="flex items-center gap-2"><SafeIcon icon={FiBriefcase} className="text-emerald-600" /><p className="text-[10px] font-black uppercase text-emerald-700">Illustrative match</p></div>
    <div className="bg-white p-3 sm:p-4 rounded-xl border border-emerald-100">
      <p className="text-[10px] text-slate-500 whitespace-nowrap tracking-tight">You contribute 6% → employer adds 3%</p>
      <div className="h-3 mt-3 bg-slate-100 rounded-full overflow-hidden flex"><div className="w-2/3 bg-blue-500" /><div className="w-1/3 bg-emerald-500" /></div>
    </div>
    <p className="text-[10px] font-bold text-emerald-800">The return depends on your employer plan. Verify its vesting rules,too.</p>
  </div>
);

const GENERAL = [
  ['Month 1 Double Principal', 'An extra principal payment early can reduce future interest. Ask your lender to apply it to principal and check for prepayment penalties.'],
  ['Biweekly Schedule', 'Twenty-six half-payments equal thirteen full payments a year,if the lender applies each payment promptly.'],
  ['0% Balance Transfers', 'Compare the transfer fee and promotional end date before moving a balance.'],
  ['The Avalanche', 'Pay minimums on every debt,then direct extra money to the highest interest rate.']
];
const MORTGAGE = [
  ['Compare loan estimates', 'Request quotes from multiple lenders and compare APR,closing costs and the rate-lock terms.'],
  ['Fixed rate and fees', 'Understand points,origination charges and whether a fixed rate fits your plans.'],
  ['Extra payments', 'Confirm how extra principal and biweekly payments are applied before setting them up.'],
  ['Escrow and taxes', 'Check lender rules before changing escrow;budget for property tax and insurance,and check local homestead eligibility.']
];

export const DebtTool = ({shared, patch}) => {
  const mortgage = shared.debtTab === 'mortgage';
  return (
    <div className="space-y-4">
      <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
        {[['general', 'General Debt'], ['mortgage', 'Mortgage & Home']].map(([value, label]) => (
          <button key={value} onClick={() => patch({debtTab: value})} className={`flex-1 py-2 text-[9px] font-black uppercase tracking-widest rounded-lg ${shared.debtTab === value ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'}`}>{label}</button>
        ))}
      </div>
      <div className="space-y-2">
        {(mortgage ? MORTGAGE : GENERAL).map(([title, description], index) => (
          <div key={title} className="flex gap-3 p-3 bg-white border border-slate-100 rounded-xl shadow-sm">
            <span className="w-6 h-6 rounded-lg bg-blue-50 text-blue-600 text-[10px] font-black flex items-center justify-center flex-shrink-0">{index + 1}</span>
            <div><p className="text-[11px] font-black text-slate-900">{title}</p><p className="text-[10px] font-medium text-slate-500 leading-relaxed">{description}</p></div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const DebtVisual = ({shared}) => {
  const mortgage = shared.debtTab === 'mortgage';
  const items = mortgage ? [
    {icon: FiHome, name: 'Shop lenders', detail: 'Compare APR and total costs.'},
    {icon: FiCreditCard, name: 'Review fees', detail: 'Know what is due at closing.'},
    {icon: FiCalendar, name: 'Plan payments', detail: 'Confirm principal and escrow rules.'}
  ] : [
    {icon: FiCreditCard, name: 'List every balance', detail: 'Write down rate,minimum and balance.'},
    {icon: FiTrendingDown, name: 'Attack the highest APR', detail: 'Keep minimums current everywhere else.'},
    {icon: FiCalendar, name: 'Automate the extra', detail: 'Track the payoff and revisit monthly.'}
  ];
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-3xl border border-slate-100 p-5 shadow-sm">
        <p className="text-[9px] font-black uppercase tracking-widest text-blue-600 mb-4">{mortgage ? 'A smarter home-loan decision' : 'A practical debt payoff path'}</p>
        <div className="space-y-3">
          {items.map((item, index) => (
            <div key={item.name} className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50 border border-slate-100">
              <div className="w-10 h-10 rounded-xl bg-white text-blue-600 shadow-sm flex items-center justify-center flex-shrink-0"><SafeIcon icon={item.icon} /></div>
              <div className="flex-1 min-w-0"><p className="text-[11px] font-black text-slate-900">{item.name}</p><p className="text-[9px] font-medium text-slate-500">{item.detail}</p></div>
              <span className="text-[10px] font-black text-slate-300">0{index + 1}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100 flex gap-3">
        <SafeIcon icon={FiZap} className="text-blue-600 flex-shrink-0" />
        <p className="text-[10px] font-semibold text-blue-900">{mortgage ? 'Ask the lender how additional payments are credited;escrow waivers and fees vary by lender and location.' : 'The best strategy is one you can keep paying consistently without draining your emergency fund.'}</p>
      </div>
    </div>
  );
};